import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string[];
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  twitterCard?: string;
  structuredData?: object;
}

const defaultProps: SEOProps = {
  title: 'Jim Root Memorial & Kusumoto Klassic Wrestling Tournaments',
  description: '63rd Annual Jim Root Memorial Boys Wrestling Tournament & 10th Annual Kusumoto Klassic Girls Wrestling Tournament at Prospect High School, Saratoga, CA. January 15-16, 2027. 32-man double elimination brackets.',
  keywords: [
    'Jim Root Memorial',
    'Kusumoto Klassic',
    'wrestling tournament',
    'high school wrestling',
    'Prospect High School',
    'Saratoga wrestling',
    'California wrestling',
    'boys wrestling tournament',
    'girls wrestling tournament',
    'NFHS wrestling',
    'double elimination',
    'youth wrestling',
    'Bay Area wrestling'
  ],
  canonical: 'https://jimrootwrestling.com',
  ogType: 'website',
  ogImage: 'https://jimrootwrestling.com/og-image.jpg',
  twitterCard: 'summary_large_image',
};

export default function SEO({
  title = defaultProps.title,
  description = defaultProps.description,
  keywords = defaultProps.keywords,
  canonical = defaultProps.canonical,
  ogType = defaultProps.ogType,
  ogImage = defaultProps.ogImage,
  twitterCard = defaultProps.twitterCard,
  structuredData,
}: SEOProps) {
  const fullTitle = title === defaultProps.title ? title : `${title} | Jim Root Wrestling`;
  const keywordString = keywords?.join(', ');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywordString} />
      )}
      <link rel="canonical" href={canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Jim Root Memorial Wrestling" />

      {/* Twitter */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:url" content={canonical} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Additional SEO Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Jim Root Memorial Wrestling Tournament" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />

      {/* Geographic Tags */}
      <meta name="geo.region" content="US-CA" />
      <meta name="geo.placename" content="Saratoga" />
      <meta name="geo.position" content="37.2638;-122.0230" />
      <meta name="ICBM" content="37.2638, -122.0230" />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
}

// Helper function to create event structured data
export function createEventStructuredData(eventName: string, eventDate: string, eventType: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    name: eventName,
    description: `${eventName} - High school wrestling tournament at Prospect High School`,
    startDate: eventDate,
    endDate: eventDate,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: 'Prospect High School',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '18900 Prospect Road',
        addressLocality: 'Saratoga',
        addressRegion: 'CA',
        postalCode: '95070',
        addressCountry: 'US',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 37.2638,
        longitude: -122.0230,
      },
    },
    organizer: {
      '@type': 'Organization',
      name: 'Jim Root Memorial Wrestling Tournament',
      url: 'https://jimrootwrestling.com',
      email: 'Jimrootwrestling@gmail.com',
      telephone: '+1-650-619-7529',
    },
    sport: 'Wrestling',
    competitor: [
      {
        '@type': 'SportsTeam',
        name: eventType === 'boys' ? 'Boys High School Wrestling Teams' : 'Girls High School Wrestling Teams',
      },
    ],
    offers: {
      '@type': 'Offer',
      url: 'https://jimrootwrestling.com/register',
      price: eventType === 'boys' ? '425' : '400',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      validFrom: '2026-08-01',
    },
  };
}

// Organization structured data
export const organizationStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'SportsOrganization',
  name: 'Jim Root Memorial Wrestling Tournament',
  url: 'https://jimrootwrestling.com',
  logo: 'https://jimrootwrestling.com/logo.png',
  email: 'Jimrootwrestling@gmail.com',
  telephone: '+1-650-619-7529',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '18900 Prospect Road',
    addressLocality: 'Saratoga',
    addressRegion: 'CA',
    postalCode: '95070',
    addressCountry: 'US',
  },
  sport: 'Wrestling',
  sponsor: [
    {
      '@type': 'Organization',
      name: 'Team Fluffy Wrestling',
    },
    {
      '@type': 'Organization',
      name: 'Lions Club',
    },
    {
      '@type': 'Organization',
      name: 'Distant Klash',
    },
  ],
};
